import { INote } from '@dataTypes/note.type'
import { generateFormPDF } from '@utils/formPdfGenerator.util'

const FALLBACK_LOGO_URL =
  'https://cdn-icons-png.flaticon.com/512/13712/13712994.png'

export interface NotePdfExportParams {
  note: INote
  patientName: string
  clinicName: string
  clinicAddress: string
  clinicLogoUrl?: string | null
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function notePdfFilename(note: INote): string {
  const base = note.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()
  return `${base}_note.pdf`
}

function formatDisplayDate(iso: string | undefined): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString()
}

async function loadUrlAsDataUrl(url: string): Promise<string | null> {
  if (typeof window === 'undefined') return null
  if (url.startsWith('data:')) return url
  try {
    const res = await fetch(url, { cache: 'force-cache', mode: 'cors' })
    if (!res.ok) return null
    const blob = await res.blob()
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = (): void => {
        const r = reader.result
        resolve(typeof r === 'string' ? r : '')
      }
      reader.onerror = (): void =>
        reject(reader.error ?? new Error('FileReader failed'))
      reader.readAsDataURL(blob)
    })
  } catch {
    return null
  }
}

export async function resolveNotePdfLogoSrc(
  clinicLogoUrl?: string | null,
): Promise<string> {
  const trimmed = clinicLogoUrl?.trim() ?? ''
  if (trimmed.length > 0) {
    const dataUrl = await loadUrlAsDataUrl(trimmed)
    if (dataUrl && dataUrl.length > 0) return dataUrl
    return trimmed
  }

  const fallbackDataUrl = await loadUrlAsDataUrl(FALLBACK_LOGO_URL)
  return fallbackDataUrl && fallbackDataUrl.length > 0
    ? fallbackDataUrl
    : FALLBACK_LOGO_URL
}

function waitForImages(root: HTMLElement): Promise<void> {
  const images = Array.from(root.querySelectorAll('img'))
  if (images.length === 0) return Promise.resolve()

  return Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve()
            return
          }
          img.addEventListener('load', () => resolve(), { once: true })
          img.addEventListener('error', () => resolve(), { once: true })
        }),
    ),
  ).then(() => undefined)
}

export function buildNotePdfCaptureHtml(
  params: NotePdfExportParams,
  logoSrc: string,
): string {
  const { note, patientName, clinicName, clinicAddress } = params
  const lastEdited =
    note.date_time_updated_at ?? note.date_time_created_at ?? ''
  const dateCreated = note.date_time_created_at ?? ''

  const addressBlock =
    clinicAddress.trim().length > 0
      ? `<div class="kiosk-pdf-header-clinic-address">${escapeHtml(
          clinicAddress.trim(),
        )}</div>`
      : ''

  const headerHtml = `<div class="kiosk-pdf-header">
  <img src="${escapeHtml(logoSrc)}" alt="Clinic logo" class="kiosk-pdf-header-logo" crossorigin="anonymous" />
  <div class="kiosk-pdf-header-text">
    <div class="kiosk-pdf-header-clinic-name">${escapeHtml(clinicName)}</div>
    ${addressBlock}
  </div>
</div>`

  const metaHtml = `<div class="notes-pdf-meta">
  <div class="notes-pdf-meta-row"><span class="notes-pdf-meta-label">Note</span><span class="notes-pdf-meta-value">${escapeHtml(note.title)}</span></div>
  <div class="notes-pdf-meta-row"><span class="notes-pdf-meta-label">Patient</span><span class="notes-pdf-meta-value">${escapeHtml(patientName)}</span></div>
  <div class="notes-pdf-meta-row"><span class="notes-pdf-meta-label">Date created</span><span class="notes-pdf-meta-value">${escapeHtml(formatDisplayDate(dateCreated))}</span></div>
  <div class="notes-pdf-meta-row"><span class="notes-pdf-meta-label">Last edited</span><span class="notes-pdf-meta-value">${escapeHtml(formatDisplayDate(lastEdited))}</span></div>
  <div class="notes-pdf-meta-row"><span class="notes-pdf-meta-label">Tag</span><span class="notes-pdf-meta-value">${escapeHtml(note.context_type ?? '')}</span></div>
</div>`

  const bodyHtml = `<div class="notes-pdf-body">${note.body ?? ''}</div>`

  return `${headerHtml}${metaHtml}${bodyHtml}`
}

export async function downloadNoteAsPdf(
  params: NotePdfExportParams,
): Promise<void> {
  const logoSrc = await resolveNotePdfLogoSrc(params.clinicLogoUrl)

  const wrapper = document.createElement('div')
  wrapper.className = 'notes-pdf-export-root'
  wrapper.setAttribute('aria-hidden', 'true')
  Object.assign(wrapper.style, {
    position: 'fixed',
    left: '-9999px',
    top: '0',
    width: '794px',
    zIndex: '-1',
    pointerEvents: 'none',
    backgroundColor: '#ffffff',
  })
  wrapper.innerHTML = buildNotePdfCaptureHtml(params, logoSrc)
  document.body.appendChild(wrapper)

  try {
    await waitForImages(wrapper)
    await new Promise<void>((resolve) => {
      window.setTimeout(() => resolve(), 50)
    })

    await new Promise<void>((resolve, reject) => {
      void generateFormPDF({
        element: wrapper,
        filename: notePdfFilename(params.note),
        onSuccess: () => resolve(),
        onError: (message) => reject(new Error(message)),
      })
    })
  } finally {
    wrapper.remove()
  }
}
